import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import L from 'leaflet';
import { Observable, Observer } from 'rxjs';
import { LayerThemeData, LegendItem } from '../data-types/map-layer-info';
import { ModalItem } from '../elements/modal-item';
import { getLimitiPopUpContent, getParameterIfFilterVisible, getValues, hasValues } from './utils';
import { WmsLayer } from './WmsLayer';
import { NonTiledLayerWMS } from './NonTiledLayer';

export class StaticLegendWmsLayer implements WmsLayer {
	name: string;
	visibleFilterIds: string[];
	modal: ModalItem;
	themes: LayerThemeData[];
	selected: boolean;
	legend: LegendItem[];

	constructor(
		name: string,
		visibleFilterIds: string[],
		modal: ModalItem,
		themes: LayerThemeData[],
		selected: boolean,
		colorLegend: LegendItem[],
	) {
		this.name = name;
		this.visibleFilterIds = visibleFilterIds;
		this.modal = modal;
		this.themes = themes;
		this.selected = selected;
		this.legend = colorLegend;
	}

	getAsWms = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}): L.Layer[] => {
		let layers = [];

		for (let theme of this.themes) {
			let layer = null;
			if (theme.tiled !== undefined && !theme.tiled) {
				layer = new NonTiledLayerWMS(`${environment.contextRoot}/api/wms?` + this.getWMSQuery(filters), {
					maxZoom: 18,
					attribution: '...',
					transparent: true,
					format: 'image/png',
					zIndex: 1000,
					layers: theme.name,
					styles: theme.style,
				});
			} else {
				layer = new L.TileLayer.WMS(`${environment.contextRoot}/api/wms?` + this.getWMSQuery(filters), {
					maxZoom: 18,
					attribution: '...',
					transparent: true,
					format: 'image/png',
					zIndex: 1000,
					layers: theme.name,
					styles: theme.style,
				});
			}

			layers.push(layer);
		}

		return layers;
	};

	/**
	 * Prepares the wms query parameter from the selected layer and the parent's filters selected values.
	 * * Tutti i filtri tranne numero strada e nome strada vogliono una @ all'inizio e alla fine e vengono separati da '@@' se ci sono più valori
	 * * alcuni, poi, come valore nullo vogliono cose specifiche (invece di ""): numStrada -> '000', limPortata -> '@@', limSagoma -> '@@', limTipologia -> '@@'
	 * @returns
	 */
	//https://docs.geoserver.org/stable/en/user/data/database/sqlview.html#using-a-parametric-sql-view
	private getWMSQuery = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}) => {
		let viewparams = [];

		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'province'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'comuni'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'numStrada', '000', '', '', ''));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'nomeStrada', '', '', '', ''));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'classAmm'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'limPortata', '@@'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'limSagoma', '@@'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'limTipologia', '@@'));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'limPuntuali', '@@'));

		return `viewparams=` + viewparams.join(';');
	};

	getPopup = (
		clickedPoint: L.LatLng,
		filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
		http: HttpClient,
		zoom: number,
	): Observable<L.Popup> => {
		var toPoint = L.Projection.SphericalMercator.project(clickedPoint);

		// set operation type based on selected layer
		var operation = 1;
		if (this.name == 'Mappa dei Limiti Puntuali') {
			operation = 3;
		}

		var params = this.getPopUpParams(toPoint, operation, filters, zoom);

		// This function runs when subscribe() is called
		function popupSubscriber(observer: Observer<L.Popup>) {
			http.get<any>(`${environment.contextRoot}/api/limiti/map/popup`, {
				responseType: 'json',
				params: params,
			}).subscribe((data) => {
				if (data.length > 0) {
					let actualLatLng = L.Projection.SphericalMercator.unproject(
						new L.Point(data[0].geometry.coordinates[0], data[0].geometry.coordinates[1]),
					);

					observer.next(
						L.popup().setLatLng(actualLatLng).setContent(getLimitiPopUpContent(data[0], operation)),
					);
				}
				observer.complete();
			});

			return { unsubscribe() {} };
		}

		return new Observable(popupSubscriber);
	};

	/**
	 * Get params for /map/popup request from filter values
	 * @param point Point object representing where the user clicked on the map
	 */
	private getPopUpParams = (
		point: L.Point,
		operation: number,
		filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
		zoom: number,
	) => {
		let result = new HttpParams();
		result = result.append('operation', operation);
		result = result.append('lat', point.y);
		result = result.append('lon', point.x);
		result = result.append('srs', 'EPSG:3785');
		result = result.append('zoomlevel', Math.round(zoom));
		result = result.append('filter_provincia', hasValues('province', filters));
		result = result.append('input_provincia', getValues('province', filters));
		result = result.append('filter_comune', hasValues('comuni', filters));
		result = result.append('input_comune', getValues('comuni', filters));
		result = result.append('filter_num_strada', hasValues('numStrada', filters));
		result = result.append('input_num_strada', getValues('numStrada', filters, '000', '', '', ''));
		result = result.append('filter_nome_strada', hasValues('nomeStrada', filters));
		result = result.append('input_nome_strada', getValues('nomeStrada', filters, '', '', '', ''));
		result = result.append('filter_class_amm_mista', hasValues('classAmm', filters));
		result = result.append('input_class_amm_mista', getValues('classAmm', filters));
		result = result.append('filter_lim_portata', hasValues('limPortata', filters));
		result = result.append('input_lim_portata', getValues('limPortata', filters, '@@'));
		result = result.append('filter_lim_sagoma', hasValues('limSagoma', filters));
		result = result.append('input_lim_sagoma', getValues('limSagoma', filters, '@@'));
		result = result.append('filter_lim_tipo', hasValues('limTipologia', filters));
		result = result.append('input_lim_tipo', getValues('limTipologia', filters, '@@'));
		result = result.append('filter_tipo_lim_puntuale', '0');
		result = result.append('filter_lim_puntuale_desc', '0');
		return result;
	};
}
